// Chat markers + pin-drop as a module singleton, deliberately outside any
// component lifecycle: watchers live in App's effect scope for the lifetime of
// the app, marker elements are plain DOM with native listeners committing
// straight to the store, and nothing ever removes them on unmount — they can
// only change when the synced state changes or the map itself is destroyed.
import { watchEffect, watch } from 'vue'
import mapboxgl from 'mapbox-gl'
import { useUserStore } from '@/stores/user'
import { useGeoStore } from '@/stores/geo'
import { useDiscussionStore } from '@/stores/discussion'

const AVATAR_PALETTE = ['bg-rose-500', 'bg-amber-500', 'bg-emerald-600', 'bg-sky-500', 'bg-violet-500', 'bg-teal-600']

const esc = (s) =>
  String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c])

function avatarHtml(author) {
  const name = author?.display_name || '?'
  if (author?.avatar_url) {
    return `<img src="${esc(author.avatar_url)}" alt="" class="h-full w-full object-cover" />`
  }
  const initials = name
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase()
  let h = 0
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) % 997
  const color = AVATAR_PALETTE[h % AVATAR_PALETTE.length]
  return `<div class="flex h-full w-full items-center justify-center text-xs font-semibold text-white ${color}">${esc(initials)}</div>`
}

function buildPinEl(pin) {
  const el = document.createElement('div')
  el.className = 'cursor-pointer'
  const stateClasses = [
    pin.act ? 'ring-2 ring-primary' : '',
    pin.ghost ? 'opacity-60' : '',
    pin.resolved ? 'opacity-75 grayscale' : '',
  ].join(' ')
  const badge = pin.replies
    ? `<div class="absolute -right-1.5 -top-1.5 h-4 min-w-4 rounded-full bg-primary px-1 text-center text-[10px] font-semibold leading-4 text-white">${pin.replies + 1}</div>`
    : ''
  const check = pin.resolved
    ? `<div class="absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-emerald-600 text-white"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" class="h-3 w-3"><path d="M20 6 9 17l-5-5"/></svg></div>`
    : ''
  el.innerHTML = `
    <div class="relative h-9 w-9 rounded-full rounded-bl-none border-2 border-white bg-white shadow-lg ${stateClasses}">
      <div class="h-full w-full overflow-hidden rounded-full rounded-bl-none">${avatarHtml(pin.author)}</div>
      ${badge}${check}
    </div>`
  if (!pin.ghost) {
    el.addEventListener('click', (e) => {
      e.stopPropagation()
      useDiscussionStore().setActiveThread(pin.id)
    })
  }
  return el
}

const sigOf = (pin) =>
  JSON.stringify([pin.location, pin.replies, !!pin.resolved, !!pin.act, !!pin.ghost, pin.author?.member_id, pin.author?.avatar_url])

const handles = new Map() // key -> { marker, sig }
let markersMap = null // the map instance the current handles belong to

function clearHandles(removeFromMap) {
  if (removeFromMap) {
    for (const { marker } of handles.values()) {
      try { marker.remove() } catch (e) { /* map already destroyed */ }
    }
  }
  handles.clear()
}

function syncMarkers(map, pins) {
  if (map !== markersMap) {
    // Map instance changed (or died) — old elements went with the old map's DOM
    clearHandles(false)
    markersMap = map
  }
  if (!map) return

  const wanted = new Map(pins.map((p) => [p.id, p]))

  for (const [key, handle] of handles) {
    const pin = wanted.get(key)
    if (!pin || handle.sig !== sigOf(pin)) {
      try { handle.marker.remove() } catch (e) { /* ignore */ }
      handles.delete(key)
    }
  }

  for (const [key, pin] of wanted) {
    if (handles.has(key)) continue
    const marker = new mapboxgl.Marker({ anchor: 'bottom-left', element: buildPinEl(pin) })
      .setLngLat(pin.location)
      .addTo(map)
    handles.set(key, { marker, sig: sigOf(pin) })
  }
}

// --- chat-only thread geometries (drawn via the Message tool) ---

const GEOMS_SOURCE = 'discussion-geoms'
let geomsMap = null
let geomsData = null

function applyGeomLayers(map) {
  if (map.getSource(GEOMS_SOURCE)) {
    map.getSource(GEOMS_SOURCE).setData(geomsData)
    return
  }
  map.addSource(GEOMS_SOURCE, { type: 'geojson', data: geomsData })
  map.addLayer({
    id: `${GEOMS_SOURCE}-fill`,
    type: 'fill',
    source: GEOMS_SOURCE,
    filter: ['==', '$type', 'Polygon'],
    paint: { 'fill-color': '#0059ff', 'fill-opacity': 0.15 },
  })
  map.addLayer({
    id: `${GEOMS_SOURCE}-line`,
    type: 'line',
    source: GEOMS_SOURCE,
    paint: { 'line-color': '#0059ff', 'line-width': 2 },
  })
}

function onGeomsStyleLoad() {
  // A basemap switch wiped the style — re-add source and layers.
  if (geomsMap && geomsData) {
    try { applyGeomLayers(geomsMap) } catch (e) { /* map is mid-teardown */ }
  }
}

function syncGeometries(map, features) {
  if (map !== geomsMap) {
    if (geomsMap) {
      try { geomsMap.off('style.load', onGeomsStyleLoad) } catch (e) { /* destroyed */ }
    }
    geomsMap = map
    if (map) map.on('style.load', onGeomsStyleLoad)
  }
  if (!map) return
  geomsData = { type: 'FeatureCollection', features }
  try {
    applyGeomLayers(map)
  } catch (e) {
    // Style not ready yet — the style.load handler re-applies.
  }
}

// --- pin drop ---

let pinDropMap = null

function onPinDrop(e) {
  useDiscussionStore().setDraftLocation([e.lngLat.lng, e.lngLat.lat])
  useDiscussionStore().setPinDrop(null)
}

function onEscape(e) {
  if (e.key !== 'Escape') return
  // Layered dismissal: the composer's mention list calls preventDefault, and
  // reka-ui popovers (emoji picker, attach menu) live in a teleported popper
  // wrapper — Escape inside either closes only that layer, not the draft.
  if (e.defaultPrevented || e.target?.closest?.('[data-reka-popper-content-wrapper]')) return
  if (useDiscussionStore().pinDrop) useDiscussionStore().setPinDrop(null)
  else if (useDiscussionStore().mapDraft) useDiscussionStore().setMapDraft(null)
}

function detachPinDrop() {
  if (!pinDropMap) return
  try {
    pinDropMap.getCanvas().style.cursor = ''
    pinDropMap.off('click', onPinDrop)
  } catch (e) { /* map already destroyed */ }
  pinDropMap = null
}

// --- init (called once from App.vue setup; watchers live in App's scope) ---

let initialized = false

export function initDiscussionMap(router) {
  if (initialized) return
  initialized = true

  const projectId = () => router.currentRoute.value.params.project_id

  // Load + reset per project
  watch(
    () => projectId(),
    (id, oldId) => {
      if (id) useDiscussionStore().ensureLoaded(id)
      if (oldId && id !== oldId) {
        useDiscussionStore().setActiveThread(null)
        useDiscussionStore().setPinDrop(null)
        useDiscussionStore().setDraftLocation(null)
        useDiscussionStore().setMapDraft(null)
      }
    },
    { immediate: true }
  )

  // Marker sync — watchEffect tracks every reactive read below
  watchEffect(() => {
    const map = useGeoStore().map
    const mapReady = useGeoStore().mapReady
    const id = projectId()
    const enabled = id && useDiscussionStore().isEnabled(id)

    const pins = []
    if (map && mapReady && enabled) {
      const activeThreadId = useDiscussionStore().activeThreadId
      const me = useUserStore().member
      for (const m of useDiscussionStore().messages(id)) {
        if (!m.location) continue
        pins.push({
          id: m.id,
          author: m.author,
          location: m.location,
          replies: m.replies?.length || 0,
          resolved: m.resolved,
          act: activeThreadId === m.id,
          ghost: false,
        })
      }
      for (const [key, location] of [
        ['__draft__', useDiscussionStore().draftLocation],
        ['__map_draft__', useDiscussionStore().mapDraft],
      ]) {
        if (location) pins.push({ id: key, author: me || {}, location, replies: 0, resolved: false, act: false, ghost: true })
      }
    }
    syncMarkers(map && mapReady ? map : null, pins)
  })

  // Thread geometries — polygons/lines carried by messages, plus the pending draft
  watchEffect(() => {
    const map = useGeoStore().map
    const mapReady = useGeoStore().mapReady
    const id = projectId()
    const enabled = id && useDiscussionStore().isEnabled(id)

    const features = []
    if (map && mapReady && enabled) {
      for (const m of useDiscussionStore().messages(id)) {
        if (m.geometry && m.geometry.type !== 'Point') {
          features.push({ type: 'Feature', geometry: m.geometry, properties: { id: m.id, resolved: m.resolved } })
        }
      }
      const draftGeometry = useDiscussionStore().mapDraftGeometry
      if (draftGeometry && draftGeometry.type !== 'Point') {
        features.push({ type: 'Feature', geometry: draftGeometry, properties: { id: '__map_draft__' } })
      }
    }
    syncGeometries(map && mapReady ? map : null, features)
  })

  // Pin-drop crosshair + one-shot click
  watchEffect(() => {
    const on = useDiscussionStore().pinDrop
    const map = useGeoStore().map
    detachPinDrop()
    if (on && map) {
      pinDropMap = map
      map.getCanvas().style.cursor = 'crosshair'
      map.once('click', onPinDrop)
    }
  })

  // Escape cancels pin modes / the compose bubble
  watchEffect(() => {
    const listening = useDiscussionStore().pinDrop || useDiscussionStore().mapDraft
    if (listening) window.addEventListener('keydown', onEscape)
    else window.removeEventListener('keydown', onEscape)
  })
}
